import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import {
  ConversationStatusSchema,
  isAwaitingGuardianApprovalStatus,
  isWritableConversationStatus,
} from '../conversation-status/conversation-status';

/**
 * One conversation, as the member looking at it reads it.
 *
 * **`otherUserId`, never the pair** — the `Match` contract's rule, for its
 * reason: which of the two the reader *is* is not information the reader needs,
 * and a payload carrying both would put "work out which one is me" in every
 * client that draws a chat row.
 *
 * **Ids and timestamps only. No name, no photograph, no last-message preview.**
 * The client hydrates the other member through the batched `GET /members?ids=…`
 * (P17a), which is composed in `ProfileVisibilityResolver` (N18) — so what a
 * member may see of another is decided in exactly one place, and this module
 * never becomes a second one. The preview is left out for the same reason it
 * would have to be moderated: a message withheld from its recipient (P20) must
 * not be readable in the list that sits above it.
 *
 * `unreadCount` and `otherLastReadAt` are **derived per reader**, not columns:
 * the same row answers differently to the two members in it. They come from the
 * one aggregating statement the list is built on, never from a count per row
 * (P14b).
 */
export const ConversationSchema = z
  .object({
    id: UuidSchema,
    /**
     * The match this conversation belongs to.
     *
     * Exposed so a client that has a match — from the "it's a match" moment, or
     * from the matches list — can open the conversation without a second route
     * or a lookup table of its own. It is the conversation's *cause*, and the
     * database holds it unique.
     */
    matchId: UuidSchema,
    otherUserId: UuidSchema,
    status: ConversationStatusSchema,
    /**
     * When the most recent message was sent, or `null` for a conversation
     * nobody has written in yet — the state every conversation starts in, since
     * it is created by a listener at the moment of the match.
     */
    lastMessageAt: z.iso.datetime().nullable(),
    /** Messages the reader has not seen. Never negative; `0` is the resting state. */
    unreadCount: z.number().int().min(0),
    /**
     * How far the **other** member has read, or `null` if they never opened it.
     *
     * This is what draws a delivered/read mark, and it is a timestamp rather
     * than a per-message flag because the timeline is ordered: everything the
     * reader sent at or before this instant has been seen.
     */
    otherLastReadAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  })
  .strict()
  .refine((conversation) => conversation.unreadCount === 0 || conversation.lastMessageAt !== null, {
    message: 'A conversation nobody has written in cannot have unread messages.',
    path: ['unreadCount'],
  });

export type Conversation = z.infer<typeof ConversationSchema>;

/** May the reader send into this conversation? */
export function isConversationWritable(conversation: Conversation): boolean {
  return isWritableConversationStatus(conversation.status);
}

/**
 * Is this conversation read-only *because a guardian has not opened it yet*?
 *
 * Beside `isConversationWritable` rather than derived from it: both states
 * refuse a write, and a client that distinguishes them only by negation says
 * "this conversation has ended" to a ward whose wali simply has not looked yet.
 */
export function isConversationAwaitingGuardian(conversation: Conversation): boolean {
  return isAwaitingGuardianApprovalStatus(conversation.status);
}
